import { Box, Alert, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';

import type { ObjectType } from '@/shared/types/permission';
import { useCheckObjectExistsQuery } from '@/features/permissions/permissionsApi';

const TYPE_LABELS: Record<string, string> = {
  course: 'course',
  unit: 'unit',
  lesson: 'lesson',
  resource: 'resource',
  rubric: 'rubric',
};

interface AccessDeniedOrNotFoundProps {
  objectType: ObjectType;
  objectId: string | undefined;
  user: User | null;
}

export default function AccessDeniedOrNotFound({
  objectType,
  objectId,
  user,
}: AccessDeniedOrNotFoundProps) {
  const skip = !user || !objectId;
  const {
    data: exists,
    isLoading,
    isError,
  } = useCheckObjectExistsQuery({ object_type: objectType, object_id: objectId! }, { skip });

  const label = TYPE_LABELS[objectType] ?? 'item';
  const capLabel = label.charAt(0).toUpperCase() + label.slice(1);

  if (!user) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          This content may be private. <Link to="/login">Sign in</Link> to access it if you have
          permission.
        </Alert>
      </Box>
    );
  }

  // While the existence check is in flight, show a spinner instead of a
  // misleading "not found" message.
  if (!skip && (isLoading || (exists === undefined && !isError))) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!skip && isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">We couldn&apos;t verify this {label}. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Alert severity="error">
        {exists === true
          ? `You don't have permission to view this ${label}.`
          : `${capLabel} not found.`}
      </Alert>
    </Box>
  );
}
